import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";

const LINES = [
  { text: "ULTRA-FINE", accent: false },
  { text: "AUTOMOTIVE", accent: false },
  { text: "SANCTUARY", accent: true },
];

const STATS = [
  { label: "Vehicles Detailed", value: "20+" },
  { label: "Years of Craft", value: "1+" },
  { label: "Repeat Clients", value: "78%" },
  { label: "Client Rating", value: "5.0 / 5.0" },
];

const HERO_VIDEO = "/media/hero.mp4";
const HERO_VIDEO_WEBM = "/media/hero.webm";
const HERO_POSTER = "/media/hero-poster.jpg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <motion.video
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-video"
          initial={{ opacity: 0, scale: 1.12 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          className="h-full w-full object-cover object-center"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
        </motion.video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#090A0E] via-[#090A0E]/55 to-[#090A0E]/30" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(9,10,14,0.75)_100%)]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-14 pt-40 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          data-testid="hero-eyebrow"
          className="mb-6 font-mono text-xs uppercase tracking-[0.35em] text-[#FFB703]"
        >
          Elite Mobile Detail — Interior · Exterior · Full Detail
        </motion.p>

        <h1 className="font-display text-[clamp(1.6rem,7vw,2.5rem)] font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-8xl">
          {LINES.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1 pr-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.55 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${line.accent ? "text-[#FFB703]" : ""}`}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          data-testid="hero-subtitle"
          className="mt-6 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base"
        >
          Deep interior restoration, meticulous exterior hand washes, and complete full-detail
          packages for daily drivers and weekend toys alike. Configure your detail below — instant, transparent pricing.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href="#estimator"
            data-testid="hero-cta-quote"
            className="group inline-flex items-center gap-2 rounded-full bg-[#FFB703] px-7 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#090A0E] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#FFA200] hover:shadow-[0_0_36px_rgba(255,183,3,0.4)]"
          >
            Configure Your Quote
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#manifesto"
            data-testid="hero-cta-craft"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-slate-200 transition-[border-color,color] duration-300 hover:border-[#FFB703]/60 hover:text-[#FFB703]"
          >
            Explore The Craft
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.5 }}
          data-testid="hero-stats-bar"
          className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 backdrop-blur-sm lg:grid-cols-4"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#0C0E14]/80 px-6 py-5">
              <p className="font-mono text-xl font-bold text-white sm:text-2xl">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-5 right-6 z-10 hidden text-slate-500 lg:block"
      >
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
}
